import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios'; // Import Axios
import { API_END_POINTS } from '../../utils/constants/apiEndPointConstants';
import { resetAllReduxStates } from './resetAllReduxStatesAction';


export const fetchCustomViews = createAsyncThunk(
  'customViews/fetchCustomViews',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_END_POINTS.collumnVisibility}/${userId}`);
      return response.data; // Ensure the API returns data in the expected format
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const saveCustomView = createAsyncThunk(
  'customViews/saveCustomView',
  async ({ userId, viewData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_END_POINTS.collumnVisibility}/${userId}`, viewData);
      return response.data; // Ensure response.data contains the expected structure
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteCustomView = createAsyncThunk(
  'customViews/deleteCustomView',
  async ({ userId, settingId }, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_END_POINTS.collumnVisibility}/${userId}/${settingId}`);
      return settingId; // Return settingId for the reducer
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateCustomView = createAsyncThunk(
  'customViews/updateCustomView',
  async ({ userId, settingId, settingName, type, settingData }, { rejectWithValue }) => {
    
    if (!settingId) {
      return rejectWithValue('Setting ID is required');
    }
    if (!settingName) {
      return rejectWithValue('Setting name is required');
    }
    if (!type) {
      return rejectWithValue('Type is required');
    }

    try {
      // Sanitize settingData to ensure no circular references
      const sanitizedSettingData = settingData.map(item => ({
        columnName: typeof item.columnName === 'string' 
          ? item.columnName 
          : item.columnName?.toString() || '',
        visible: Boolean(item.visible)
      }));

      const payload = {
        settingName,
        type,
        settingData: sanitizedSettingData
      };

      const url = `${API_END_POINTS.collumnVisibility}/${userId}/${settingId}`;
      
      const response = await axios.put(url, payload);
      return {
        id: settingId,
        ...response.data
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Rest of the slice remains the same
const initialState = {
  views: [],
  loading: false,
  error: null,
  activeView: [],
  columnVisibility: {},
}

const customViewsSlice = createSlice({
  name: 'customViews',
  initialState,
  reducers: {
    setActiveView: (state, action) => {
      state.activeView = action.payload;
    },
    setColumnVisibility: (state, action) => {
      const { tableId, visibleColumns } = action.payload;
      state.columnVisibility[tableId] = visibleColumns;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetAllReduxStates, () => initialState)
      .addCase(fetchCustomViews.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomViews.fulfilled, (state, action) => {
        state.loading = false;
        state.views = action.payload.data || action.payload; // Adjust based on API response
      })
      .addCase(fetchCustomViews.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch views';
      })
      .addCase(saveCustomView.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveCustomView.fulfilled, (state, action) => {
        state.loading = false;
        state.views.push(action.payload.data || action.payload); // Adjust based on API response
        state.activeView = (action.payload.data || action.payload).id;
      })
      .addCase(saveCustomView.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to save view';
      })
      .addCase(deleteCustomView.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCustomView.fulfilled, (state, action) => {
        state.loading = false;
        state.views = state.views.filter(view => view.id !== action.payload);
        if (state.activeView === action.payload) {
          state.activeView = null;
        }
      })
      .addCase(deleteCustomView.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete view';
      })
      .addCase(updateCustomView.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCustomView.fulfilled, (state, action) => {
        state.loading = false;
        const updatedView = action.payload;
        const index = state.views.findIndex(view => view.id === updatedView.id);
        if (index !== -1) {
          state.views[index] = updatedView;
        }
        if (state.activeView === updatedView.id) {
          state.activeView = updatedView.id;
        }
      })
      .addCase(updateCustomView.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update view';
      });
  },
});

export const { setActiveView ,setColumnVisibility} = customViewsSlice.actions;
export default customViewsSlice.reducer;